import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { COUNTRY_CODES, findCountryByCode } from '../lib/countryCodes';
import { cn } from '../lib/utils';

// Reusable country-code (searchable) + phone-number field, shared by Login and Signup.
export const PhoneInput = ({
  countryCode,
  phone,
  onCountryCodeChange,
  onPhoneChange,
  error,
  disabled,
  testIdPrefix = 'phone',
}) => {
  const [open, setOpen] = useState(false);
  const selected = findCountryByCode(countryCode);

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className="h-12 w-[108px] shrink-0 justify-between px-3 font-normal"
              data-testid={`${testIdPrefix}-country-trigger`}
            >
              <span>
                {selected.country} {selected.code}
              </span>
              <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[240px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search country..." />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {COUNTRY_CODES.map((c) => (
                    <CommandItem
                      key={c.country}
                      value={`${c.name} ${c.code} ${c.country}`}
                      onSelect={() => {
                        onCountryCodeChange(c.code);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          c.code === countryCode && c.country === selected.country
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {c.country} {c.code} — {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="Phone number"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, '').slice(0, 15))}
          disabled={disabled}
          className="h-12 flex-1"
          data-testid={`${testIdPrefix}-number-input`}
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
